import { lazy, type ReactNode } from 'react';
import { matchPath } from 'react-router-dom';
import {
  BankOutlined,
  DatabaseOutlined,
  ExperimentOutlined,
  LineChartOutlined,
  LogoutOutlined,
  SafetyCertificateOutlined,
  StockOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';

import type { CurrentUser } from '@/features/auth/types';
import { AuthenticatedLoginRoute } from './AuthenticatedLoginRoute';
import { NoAccessibleRoutePage } from './NoAccessibleRoutePage';

const FundListPage = lazy(() => import('@/pages/Fund/FundListPage'));
const FundDetailPage = lazy(() => import('@/pages/Fund/FundDetailPage'));
const VolumePriceRadarPage = lazy(() => import('@/pages/Trading/VolumePriceRadarPage'));
const FactorListPage = lazy(() => import('@/pages/Factor/FactorListPage'));
const FactorDetailPage = lazy(() => import('@/pages/Factor/FactorDetailPage'));
const FactorCorrelationPage = lazy(() => import('@/pages/Factor/FactorCorrelationPage'));
const FactorMonitorPage = lazy(() => import('@/pages/Factor/FactorMonitorPage'));
const ClassificationManagementPage = lazy(
  () => import('@/pages/Classifications/ClassificationManagementPage')
);
const InstrumentComparePage = lazy(() => import('@/pages/Research/InstrumentComparePage'));
const LeadingStockPage = lazy(() => import('@/pages/Research/LeadingStockPage'));
const StockResearchPage = lazy(() => import('@/pages/Research/StockResearchPage'));
const LeadingStockHierarchyTrendPage = lazy(
  () => import('@/pages/Research/LeadingStockHierarchyTrendPage')
);
const RiskPage = lazy(() => import('@/pages/Risk/RiskPage'));
const StrategyListPage = lazy(() => import('@/pages/Strategy/StrategyListPage'));
const StrategyDetailPage = lazy(() => import('@/pages/Strategy/StrategyDetailPage'));

type Capability = string;

export interface AppRouteDefinition {
  id: string;
  path?: string;
  index?: boolean;
  element?: ReactNode;
  redirectTo?: string;
  children?: AppRouteDefinition[];
  isPublic?: boolean;
  layout?: 'main' | 'auth' | 'bare';
  menu?: {
    key: string;
    group?: string;
    label: string;
    icon?: ReactNode;
    hidden?: boolean;
  };
  menuKey?: string;
  requiredCapabilities: Capability[];
  defaultRank?: number;
}

export type AppMenuItem = NonNullable<NonNullable<MenuProps['items']>[number]>;

export interface AppRouterRouteEntry {
  definition: AppRouteDefinition;
  path?: string;
  index?: boolean;
}

export const LOGOUT_MENU_KEY = '__logout';
export const NO_ACCESS_ROUTE_PATH = '/no-access';

export const appRouteDefinitions: AppRouteDefinition[] = [
  {
    id: 'root-index',
    path: '/',
    index: true,
    layout: 'main',
    requiredCapabilities: [],
  },
  {
    id: 'fund',
    path: '/fund',
    element: <FundListPage />,
    layout: 'main',
    menu: { key: '/fund', label: '基金', icon: <BankOutlined /> },
    requiredCapabilities: ['fund:read'],
    defaultRank: 10,
  },
  {
    id: 'fund-detail',
    path: '/fund/:id',
    element: <FundDetailPage />,
    layout: 'main',
    menuKey: '/fund',
    requiredCapabilities: ['fund:read'],
  },
  {
    id: 'trading-group',
    layout: 'main',
    menu: { key: 'trading-group', label: '交易', icon: <StockOutlined /> },
    requiredCapabilities: [],
    children: [
      {
        id: 'trading-index',
        path: '/trading',
        redirectTo: '/trading/volume-price',
        layout: 'main',
        menuKey: '/trading/volume-price',
        requiredCapabilities: ['trading:read'],
      },
      {
        id: 'trading-volume-price',
        path: '/trading/volume-price',
        element: <VolumePriceRadarPage />,
        layout: 'main',
        menu: {
          key: '/trading/volume-price',
          group: 'trading-group',
          label: '量价异动',
        },
        requiredCapabilities: ['trading:read'],
        defaultRank: 20,
      },
    ],
  },
  {
    id: 'strategy',
    path: '/strategy',
    element: <StrategyListPage />,
    layout: 'main',
    menu: { key: '/strategy', label: '策略', icon: <LineChartOutlined /> },
    requiredCapabilities: ['strategy:read'],
    defaultRank: 30,
  },
  {
    id: 'strategy-detail',
    path: '/strategy/:strategyId',
    element: <StrategyDetailPage />,
    layout: 'main',
    menuKey: '/strategy',
    requiredCapabilities: ['strategy:read'],
  },
  {
    id: 'factor-group',
    layout: 'main',
    menu: { key: 'factor-group', label: '因子', icon: <ExperimentOutlined /> },
    requiredCapabilities: [],
    children: [
      {
        id: 'factor',
        path: '/factor',
        element: <FactorListPage />,
        layout: 'main',
        menu: { key: '/factor', group: 'factor-group', label: '因子列表' },
        requiredCapabilities: ['factor:read'],
        defaultRank: 50,
      },
      {
        id: 'factor-correlation',
        path: '/factor/correlation',
        element: <FactorCorrelationPage />,
        layout: 'main',
        menu: { key: '/factor/correlation', group: 'factor-group', label: '因子相关性' },
        requiredCapabilities: ['factor:read'],
      },
      {
        id: 'factor-monitor',
        path: '/factor/monitor',
        element: <FactorMonitorPage />,
        layout: 'main',
        menu: { key: '/factor/monitor', group: 'factor-group', label: '因子监控' },
        requiredCapabilities: ['factor:read'],
      },
      {
        id: 'factor-detail',
        path: '/factor/:factorId',
        element: <FactorDetailPage />,
        layout: 'main',
        menuKey: '/factor',
        requiredCapabilities: ['factor:read'],
      },
    ],
  },
  {
    id: 'data-group',
    layout: 'main',
    menu: { key: 'data-group', label: '数据', icon: <DatabaseOutlined /> },
    requiredCapabilities: [],
    children: [
      {
        id: 'classification-management',
        path: '/data/classifications',
        element: <ClassificationManagementPage />,
        layout: 'main',
        menu: { key: '/data/classifications', group: 'data-group', label: '分类' },
        requiredCapabilities: ['data:read'],
        defaultRank: 60,
      },
      {
        id: 'classification-categories',
        path: '/data/classifications/categories',
        element: <ClassificationManagementPage />,
        layout: 'main',
        menuKey: '/data/classifications',
        requiredCapabilities: ['data:read'],
      },
      {
        id: 'classification-instruments',
        path: '/data/classifications/instruments',
        element: <ClassificationManagementPage />,
        layout: 'main',
        menuKey: '/data/classifications',
        requiredCapabilities: ['data:read'],
      },
      {
        id: 'classification-ai-review',
        path: '/data/classifications/ai-review',
        element: <ClassificationManagementPage />,
        layout: 'main',
        menuKey: '/data/classifications',
        requiredCapabilities: ['data:read'],
      },
    ],
  },
  {
    id: 'research-group',
    layout: 'main',
    menu: { key: 'research-group', label: '研究', icon: <ExperimentOutlined /> },
    requiredCapabilities: [],
    children: [
      {
        id: 'stock-research',
        path: '/research/stocks',
        element: <StockResearchPage />,
        layout: 'main',
        menu: {
          key: '/research/stocks',
          group: 'research-group',
          label: '股票研究',
        },
        requiredCapabilities: ['research:read'],
        defaultRank: 70,
      },
      {
        id: 'stock-research-detail',
        path: '/research/stocks/:code',
        element: <StockResearchPage />,
        layout: 'main',
        menuKey: '/research/stocks',
        requiredCapabilities: ['research:read'],
      },
      {
        id: 'leading-stock',
        path: '/research/leading-stock',
        element: <LeadingStockPage />,
        layout: 'main',
        menu: {
          key: '/research/leading-stock',
          group: 'research-group',
          label: '龙头股研究',
        },
        requiredCapabilities: ['research:read'],
        defaultRank: 80,
      },
      {
        id: 'leading-stock-hierarchy-trend',
        path: '/research/leading-stock/hierarchy-trend',
        element: <LeadingStockHierarchyTrendPage />,
        layout: 'main',
        menuKey: '/research/leading-stock',
        requiredCapabilities: ['research:read'],
      },
      {
        id: 'instrument-compare',
        path: '/research/instrument-compare',
        element: <InstrumentComparePage />,
        layout: 'main',
        menu: {
          key: '/research/instrument-compare',
          group: 'research-group',
          label: '投资标的对比',
        },
        requiredCapabilities: ['research:read'],
        defaultRank: 90,
      },
    ],
  },
  {
    id: 'risk',
    path: '/risk',
    element: <RiskPage />,
    layout: 'main',
    menu: { key: '/risk', label: '风险', icon: <SafetyCertificateOutlined /> },
    requiredCapabilities: ['risk:read'],
    defaultRank: 90,
  },
  {
    id: 'login',
    path: '/login',
    element: <AuthenticatedLoginRoute />,
    isPublic: true,
    layout: 'auth',
    requiredCapabilities: [],
  },
  {
    id: 'no-access',
    path: NO_ACCESS_ROUTE_PATH,
    element: <NoAccessibleRoutePage />,
    layout: 'main',
    requiredCapabilities: [],
  },
];

export function flattenRouteDefinitions(
  definitions: AppRouteDefinition[] = appRouteDefinitions
): AppRouteDefinition[] {
  return definitions.flatMap((definition) => [
    definition,
    ...flattenRouteDefinitions(definition.children ?? []),
  ]);
}

function normalizePathname(pathname: string): string {
  const [pathWithoutQuery] = pathname.split(/[?#]/);
  const normalized = pathWithoutQuery || '/';

  if (normalized === '/') {
    return normalized;
  }

  return normalized.replace(/\/+$/, '');
}

function pathMatches(pattern: string, pathname: string): boolean {
  return matchPath({ path: pattern, end: true }, pathname) !== null;
}

export function findRouteDefinition(pathname: string): AppRouteDefinition | undefined {
  const normalizedPathname = normalizePathname(pathname);

  return flattenRouteDefinitions().find(
    (definition) => definition.path && pathMatches(definition.path, normalizedPathname)
  );
}

function userHasCapability(user: CurrentUser | null, capability: Capability): boolean {
  return Boolean(
    user && (user.capabilities.includes('*') || user.capabilities.includes(capability))
  );
}

export function isRouteAccessible(
  definition: AppRouteDefinition,
  user: CurrentUser | null
): boolean {
  if (definition.isPublic) {
    return true;
  }

  return definition.requiredCapabilities.every((capability) => userHasCapability(user, capability));
}

function buildMenuItem(
  definition: AppRouteDefinition,
  user: CurrentUser | null
): AppMenuItem | null {
  if (!definition.menu || definition.menu.hidden) {
    return null;
  }

  const childItems =
    definition.children
      ?.map((child) => buildMenuItem(child, user))
      .filter((item): item is AppMenuItem => item !== null) ?? [];

  if (childItems.length > 0) {
    return {
      key: definition.menu.key,
      icon: definition.menu.icon,
      label: definition.menu.label,
      children: childItems,
    };
  }

  if (definition.children && childItems.length === 0) {
    return null;
  }

  if (!isRouteAccessible(definition, user)) {
    return null;
  }

  return {
    key: definition.menu.key,
    icon: definition.menu.icon,
    label: definition.menu.label,
  };
}

export function buildAccessibleMenuItems(user: CurrentUser | null): AppMenuItem[] {
  return appRouteDefinitions
    .map((definition) => buildMenuItem(definition, user))
    .filter((item): item is AppMenuItem => item !== null);
}

export function buildMainMenuItems(user: CurrentUser | null): AppMenuItem[] {
  const routeItems = buildAccessibleMenuItems(user);

  if (!user) {
    return routeItems;
  }

  return [
    ...routeItems,
    {
      key: LOGOUT_MENU_KEY,
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];
}

export function getSelectedMenuKey(pathname: string): string | undefined {
  const definition = findRouteDefinition(pathname);

  if (!definition) {
    return undefined;
  }

  if (definition.menuKey) {
    return definition.menuKey;
  }

  if (definition.menu && !definition.menu.hidden) {
    return definition.menu.key;
  }

  return undefined;
}

export function getOpenMenuKeys(pathname: string): string[] {
  const selectedKey = getSelectedMenuKey(pathname);
  const selectedRoute = flattenRouteDefinitions().find(
    (definition) => definition.menu?.key === selectedKey
  );
  const groupKey = selectedRoute?.menu?.group;

  return groupKey ? [groupKey] : [];
}

export function getDefaultRoutePath(user: CurrentUser | null): string {
  const defaultRoute = flattenRouteDefinitions()
    .filter(
      (definition) =>
        definition.path &&
        definition.defaultRank !== undefined &&
        !definition.redirectTo &&
        isRouteAccessible(definition, user)
    )
    .sort((left, right) => left.defaultRank! - right.defaultRank!)[0];

  if (defaultRoute?.path) {
    return defaultRoute.path;
  }

  return user ? NO_ACCESS_ROUTE_PATH : '/login';
}

export function getRouterRouteEntries(layout: AppRouteDefinition['layout']): AppRouterRouteEntry[] {
  return flattenRouteDefinitions()
    .filter((definition) => definition.layout === layout)
    .flatMap<AppRouterRouteEntry>((definition) => {
      if (definition.index) {
        return [{ definition, index: true }];
      }

      return definition.path ? [{ definition, path: definition.path }] : [];
    });
}
